import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { HeaderComponent } from '@components/header/header.component'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Location, NgIf } from '@angular/common'
import { ClaimInfoComponent } from './claim-info/claim-info.component'
import { ClaimMaterialSelectionComponent } from './claim-material-selection/claim-material-selection.component'
import { CreateClaimTabInfo } from '../../interfaces/create-claim-tab-info'
import { ButtonComponent } from '@components/button/button.component'
import { ClaimInformation } from '../../interfaces/claim-information'
import { ProgressBarComponent } from '@components/progress-bar/progress-bar.component'
import { ProgressStep } from '../../interfaces/progress-step.interface'
import { ClaimClassificationComponent } from './claim-classification/claim-classification.component'
import { ClaimWarrantyComponent } from './claim-warranty/claim-warranty.component'
import { ClaimDocumentationComponent } from './claim-documentation/claim-documentation.component'
import { ClaimSummaryOnCreationComponent } from './claim-summary/claim-summary.component'
import { ApiService, UserInfo } from '../../services/api.service'
import { LoadingService } from '../../services/loading.service'
import _ from 'lodash'
import { AnalyticsService } from 'app/services/analytics.service'
import { RelatedFabricator } from '../../interfaces/partner-store'


@Component({
    selector: 'app-create-claim',
    standalone: true,
    imports: [
        HeaderComponent,
        TranslateModule,
        NgIf,
        ClaimInfoComponent,
        ClaimMaterialSelectionComponent,
        ButtonComponent,
        ProgressBarComponent,
        ClaimClassificationComponent,
        ClaimWarrantyComponent,
        ClaimDocumentationComponent,
        ClaimSummaryOnCreationComponent,
    ],
    templateUrl: './create-claim.component.html',
    styleUrl: './create-claim.component.scss',
})
export class CreateClaimComponent implements OnInit{

    userInfo: UserInfo;
    isInitialLoadingDone: boolean = false
    showFinishModal: boolean = false
    tabInfo: CreateClaimTabInfo = {
        currentTab: 'claim-info',
        previousTab: null,
        nextTab: 'material-selection',
    }

    steps: ProgressStep[] = []

    newClaim: ClaimInformation = <ClaimInformation>{
        claimInfo: {},
        selectedMaterials: [],
        claimClassification: {},
        warrantyData: {},
        documentation: {
            checks: [],
            documents: [],
            additionalInfo: [],
        },
        updated: false
    }
    initialClaimInfo = _.cloneDeep(this.newClaim)

    relatedFabricators: RelatedFabricator[]

    needWarranty = false

    claimId: string | null = null
    canContinue: boolean

    constructor(
        private router: Router,
        private changeDetectorRef: ChangeDetectorRef,
        private translate: TranslateService,
        private activeRoute: ActivatedRoute,
        private api: ApiService,
        private location: Location,
        private loading: LoadingService,
        private analytics: AnalyticsService
    ) {
        const claimId = this.activeRoute.snapshot.params['claimId']
        if (claimId) {
            this.claimId = claimId
            this.fetchDraftClaim(this.claimId!)
        } else {
            this.isInitialLoadingDone = true
        }
        this.steps = [
            {
                title: '1. ' + this.translate.instant('create-claim.claim-info'),
                status: 'active',
            },
            {
                title: '2. ' + this.translate.instant('create-claim.materials'),
                status: 'pending',
            },
            {
                title: '3. ' + this.translate.instant('create-claim.classification-step'),
                status: 'pending',
            },
            // {
            //     title: '4. ' + this.translate.instant('create-claim.warranty'),
            //     status: 'pending',
            // },
            {
                title: '4. ' + this.translate.instant('create-claim.documentation-step'),
                status: 'pending',
            },
            {
                title: '5. ' + this.translate.instant('create-claim.summary-step'),
                status: 'pending',
            },
        ]
    }

    ngOnInit() {
        this.api.userInfo.subscribe((userInfo) => {
            this.userInfo = userInfo
        })
    }

    fetchDraftClaim(claimId: string) {
        this.loading.show()
        this.api.getClaimDraft(claimId).subscribe((response) => {
            this.loading.hide()
            this.newClaim = response
            this.initialClaimInfo = _.cloneDeep(response)
            if(this.checkIfWarrantyNeeded()){
                this.addWarrantyStep()
            }
            this.isInitialLoadingDone = true
            this.setLastTab()
        })
    }

    fetchRelatedFabricators(claimId: string) {
        this.api.getRelatedFabricators(claimId).subscribe({
            next: (response) => {
                this.relatedFabricators = response
            }, error: () => {
                console.error('Error getting related fabricators in claim ' + claimId)
                this.relatedFabricators = []
            }
        })
    }

    goBack() {
        this.router.navigate(['my-claims'])
    }

    goToPrevTab() {
        if (this.tabInfo.previousTab) {
            const currentStepIndex = this.getStepIndex(this.tabInfo.currentTab);

            this.analytics.trackClick('step-back', undefined, {
                type: `claims_step${currentStepIndex}`,
                click_type: 'button',
                push_origin: 'claims'
            });
            this.tabInfo.currentTab = this.tabInfo.previousTab
            this.actionsOnTabChange()
        }
    }

    getStepIndex(tab: string): number {
        const order = ['claim-info', 'material-selection', 'classification', 'warranty', 'documentation', 'summary'];
        return order.indexOf(tab) + 1;
    }

    getStepAnalyticsPayload(tab: string): Record<string, any> {
        const claim = this.newClaim;

        switch (tab) {
            case 'claim-info':
                return {
                    flow_name: 'claims',
                    form_option: claim.claimInfo.id || 'unknown',
                    form_option1: 'not-available',
                    step: '1_claim-information',
                    push_origin: 'claims'
                };

            case 'material-selection':
                return {
                    flow_name: 'claims',
                    form_option: claim.selectedMaterials.map(m => m.invoiceData?.materialId).join(', ') || 'unknown',
                    form_option1: claim.selectedMaterials.map(m => m.invoiceData?.cost).join(', ') || 'not-available',
                    step: '2_client-data',
                    push_origin: 'claims'
                };

            case 'classification':
                return {
                    flow_name: 'claims',
                    form_option: claim.claimClassification.productType || 'not-available',
                    form_option1: claim.claimClassification.claimPlace || 'not-available',
                    form_option2: claim.claimClassification.claimType || 'not-available',
                    step: '3_claim-classification',
                    push_origin: 'claims'
                };

            case 'warranty':
                return {
                    flow_name: 'claims',
                    form_option: claim.warrantyData.caseNumber || 'not-available',
                    form_option1: claim.warrantyData.homeOwnerCity || 'not-available',
                    form_option2: claim.warrantyData.homeOwnerState || 'not-available',
                    step: '4_warranty-info',
                    push_origin: 'claims'
                };

            case 'documentation':
                return {
                    flow_name: 'claims',
                    form_option: `break_${claim.documentation ? 'yes' : 'no'}`,
                    form_option1: 'not-available',
                    form_option2: 'not-available',
                    step: '5_add-documentation',
                    push_origin: 'claims'
                };

            case 'summary':
                return {
                    flow_name: 'claims',
                    form_option: 'not-available',
                    form_option1: 'not-available',
                    form_option2: 'not-available',
                    step: '6_confirm-claim',
                    push_origin: 'claims'
                };

            default:
                return {};
        }
    }


    goToNextTab() {
        if (this.tabInfo.nextTab) {
            if (this.tabInfo.nextTab === 'finish') {
                this.sendCompletedClaim()
            } else {
                const lastModifiedTab = this.newClaim.lastModifiedTab
                const currentTabBeforeAdvance = this.tabInfo.currentTab
                this.newClaim.lastModifiedTab = undefined
                this.initialClaimInfo.lastModifiedTab = undefined

                 const trackStep = () => {
                    this.analytics.trackCustom('claims', this.getStepAnalyticsPayload(currentTabBeforeAdvance));
                    this.tabInfo.currentTab = this.tabInfo.nextTab!;
                    this.actionsOnTabChange()
                };

                if (_.isEqual(this.newClaim, this.initialClaimInfo)) {
                    this.newClaim.lastModifiedTab = lastModifiedTab
                    trackStep()
                } else {
                    this.newClaim.lastModifiedTab = lastModifiedTab
                    this.saveDraft().then(() => {
                        trackStep()
                    })
                }
            }
        }
    }

    actionsOnTabChange(){
        switch (this.tabInfo.currentTab){
            case 'material-selection':
                this.fetchRelatedFabricators(this.claimId!)
        }
    }

    saveDraft(goHome = false): Promise<void> {
        console.log('Saving draft claim:', this.newClaim);

        return new Promise((resolve) => {
            this.loading.show();

            this.api.saveClaimDraft(this.newClaim).subscribe((response) => {
                this.loading.hide()
                console.log('Draft claim saved successfully:', response)

                this.analytics.trackClick('save-draft', undefined, {
                    type: `claims_step${this.getStepIndex(this.tabInfo.currentTab)}`,
                    click_type: 'link',
                    push_origin: 'claims'
                });

                if (goHome) {
                    this.router.navigate(['my-claims'])
                } else {
                    this.newClaim = response
                    this.newClaim.updated = false
                    if (!this.claimId) {
                        this.claimId = this.newClaim.claimInfo.id!
                        this.location.replaceState(`/create-claim/${this.claimId}`)
                    }
                    this.newClaim.documentation.cleanup = undefined
                    this.newClaim.cleanupSelectedMaterials = undefined
                    this.initialClaimInfo = _.cloneDeep(this.newClaim)
                }
                resolve()
            })
        })
    }

    sendCompletedClaim() {
        console.log('Sending completed claim:', this.newClaim)
        this.api.saveClaim(this.newClaim).subscribe((response) => {
            if(response){
                console.log('Claim sent successfully:', response)
                this.showFinishModal = true

            } else {
                alert('Something went wrong. Please try again.')
            }
        })

    }
    
    onTabInit(newTabInfo: CreateClaimTabInfo) {
        this.tabInfo = newTabInfo
        this.updateProgressBar()
        this.changeDetectorRef.detectChanges()
    }

    onClaimUpdate(newClaim: ClaimInformation) {
        this.newClaim = newClaim
    }

    addWarrantyStep() {
        // This is added only in step 3. Need to move 4 and 5 to 5 and 6 and add warranty step as 4
        if (this.steps.length === 5) {
            this.needWarranty = true
            this.steps.splice(
                3,
                2,
                {
                    title: '4. ' + this.translate.instant('create-claim.warranty-step'),
                    status: 'pending',
                },
                {
                    title: '5. ' + this.translate.instant('create-claim.documentation-step'),
                    status: 'pending',
                },
                {
                    title: '6. ' + this.translate.instant('create-claim.summary-step'),
                    status: 'pending',
                }
            )
        }
    }

    removeWarrantyStep() {
        // This is added only in step 3. Need to move 4 and 5 to 5 and 6 and add warranty step as 4
        if (this.steps.length === 6) {
            this.needWarranty = false
            this.steps.splice(3, 1)
            this.steps[3].title = '4. ' + this.translate.instant('create-claim.documentation-step')
            this.steps[4].title = '5. ' + this.translate.instant('create-claim.summary-step')
        }
    }

    updateProgressBar() {
        switch (this.tabInfo.currentTab) {
            case 'claim-info':
                this.steps[0].status = 'active'
                this.steps[1].status = 'pending'
                break
            case 'material-selection':
            case 'material-edit-area':
                this.updateAllPrevious(0, 'complete')
                this.steps[1].status = 'active'
                this.steps[2].status = 'pending'
                break
            case 'classification':
                this.updateAllPrevious(1, 'complete')
                this.steps[2].status = 'active'
                this.steps[3].status = 'pending'
                break
            case 'warranty':
                this.updateAllPrevious(2, 'complete')
                this.steps[3].status = 'active'
                this.steps[4].status = 'pending'
                break
            case 'documentation':
                if (this.steps.length === 6) {
                    this.updateAllPrevious(3, 'complete')
                    this.steps[4].status = 'active'
                    this.steps[5].status = 'pending'
                } else {
                    this.updateAllPrevious(2, 'complete')
                    this.steps[3].status = 'active'
                    this.steps[4].status = 'pending'
                }
                break
            case 'summary':
                if (this.steps.length === 6) {
                    this.updateAllPrevious(4, 'complete')
                    this.steps[5].status = 'active'
                } else {
                    this.updateAllPrevious(3, 'complete')
                    this.steps[4].status = 'active'
                }
                break
        }
    }

    updateAllPrevious(id: number, status: 'complete' | 'active' | 'pending'){
        for(let i = id; i >= 0; i--){
            this.steps[i].status = status
        }
    }

    private checkIfWarrantyNeeded() {
        return this.newClaim.claimClassification.claimPlace &&
            this.newClaim.claimClassification.claimPlace !== 'in_shop';
    }

    private setLastTab() {
        const {lastModifiedTab, claimInfo} = this.newClaim
        if(!claimInfo.id || !lastModifiedTab){
            return;
        }
        this.tabInfo.currentTab = lastModifiedTab
        this.actionsOnTabChange()
    }

    getButtonLabel(): string {
        if (this.tabInfo.nextTab === 'finish') {
            return 'global.finish'
        }
        return 'create-claim.continue'
    }
    
    public closeFinishModal(): void {
        this.showFinishModal = false
        this.router.navigate(['my-claims'])
    }

}